/* eslint-disable complexity */
import { useContext, useEffect, useState, useLayoutEffect } from 'react';
import { useOnElementResize } from '../../utils/useOnElementResize.js';
import { ComboBoxContext } from '../../ComboBoxCTX.js';
import type { DSComboboxT } from '../../react-desc-prop-types.js';

const getGroupedPillWidth = (remainingPills: number): number => {
  if (remainingPills === 0) return 0;
  const digits = remainingPills.toString().length;
  return 8 * (digits - 1) + 31;
};
const useGroupPills = (): number => {
  const {
    props: { selectedValues, isNonClearable, disabled, innerRef, applyAriaDisabled: applyAriaDisabledToContainer },
    selectedOptionsRef,
    controlsWrapperRef,
    pillGroupRef,
  } = useContext(ComboBoxContext);

  const multiSelectedValues = selectedValues as DSComboboxT.ItemOption[];

  // 30 represent 30 pills maximum to calculate the grouped pill. this fix the load time issue
  // when combobox is receiving a huge amount of options.
  const selectedValuesLength = Math.min(30, multiSelectedValues.length);

  const [pillsToShow, setPillsToShow] = useState(selectedValuesLength);
  const { width } = useOnElementResize(controlsWrapperRef);
  const { width: widthInput } = useOnElementResize(innerRef || null);
  useEffect(() => {
    if (selectedValuesLength !== pillsToShow) {
      setPillsToShow(selectedValuesLength);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedValuesLength, width, widthInput]);

  useLayoutEffect(() => {
    if (selectedOptionsRef.current && pillGroupRef.current) {
      if (pillGroupRef.current) pillGroupRef.current.style.width = 'max-content';
      let lastPillFit = -1;
      let currentWidth = 0;
      const referenceWidth = selectedOptionsRef.current.offsetWidth;
      const pills: HTMLDivElement[] = [
        ...pillGroupRef.current.querySelectorAll(
          disabled || applyAriaDisabledToContainer || isNonClearable
            ? '.ds-pill-wrapper-value'
            : '.ds-pill-wrapper-removable, .ds-pill-wrapper-label',
        ),
      ] as HTMLDivElement[];
      const pillsWidth = pills.map((pill) => pill?.offsetWidth);
      pillsWidth.forEach((pillWidth, idx) => {
        currentWidth += pillWidth;
        if (currentWidth + getGroupedPillWidth(selectedValuesLength - idx - 1) <= referenceWidth) {
          lastPillFit = idx;
        }
      });

      if (!(selectedValuesLength > 0 && pillsToShow !== selectedValuesLength)) {
        const nextPillsToShow = lastPillFit + 1;

        if (nextPillsToShow !== pillsToShow) {
          setPillsToShow(nextPillsToShow);
        }
      }

      if (pillGroupRef.current) pillGroupRef.current.style.width = 'fit-content';
    }
  }, [
    selectedOptionsRef,
    innerRef,
    multiSelectedValues,
    pillsToShow,
    pillGroupRef,
    width,
    widthInput,
    disabled,
    applyAriaDisabledToContainer,
    selectedValuesLength,
    isNonClearable,
  ]);

  return pillsToShow || 1;
};

export { useGroupPills };
